import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Database, HardDriveDownload, RefreshCcw } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { CompletudeOps, CompletudeResult } from "../types";

interface CompletudePanelProps {
  data?: CompletudeResult;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRefresh: () => void;
}

const formatDatePTBR = (dateStr?: string) => {
  if (!dateStr) return "—";
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
};

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const pct = (baixadas: number, esperadas: number) =>
  esperadas === 0 ? 1 : baixadas / esperadas;

/** Medidor de uma franquia: trilho recessivo + preenchimento por status */
function Meter({ ratio, completo }: { ratio: number; completo: boolean }) {
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={`h-2 rounded-full transition-all duration-300 ${
          completo ? "bg-emerald-500" : "bg-amber-500"
        }`}
        style={{ width: `${Math.max(ratio * 100, ratio > 0 ? 2 : 0)}%` }}
      />
    </div>
  );
}

function OpsRow({ linha }: { linha: CompletudeOps }) {
  const [aberto, setAberto] = useState(false);
  const completo = linha.faltantes === 0;
  const ratio = pct(linha.baixadas, linha.esperadas);

  return (
    <div className="border-b last:border-b-0">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        {/* w-44 + truncate: nomes longos (masterfortaleza, ppprimebru) não empurram o medidor */}
        <span className="w-44 shrink-0 truncate font-bold uppercase" title={linha.ops}>
          {linha.ops}
        </span>

        <div
          className="min-w-[140px] flex-1"
          title={`${linha.baixadas} de ${linha.esperadas} nota(s) baixada(s)`}
        >
          <Meter ratio={ratio} completo={completo} />
        </div>

        <span className="w-28 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
          {linha.baixadas}/{linha.esperadas}
        </span>

        <span className="w-14 shrink-0 text-right text-sm font-semibold tabular-nums">
          {Math.round(ratio * 100)}%
        </span>

        {completo ? (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 border-emerald-200 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Completo
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="shrink-0 gap-1 border-amber-200 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            {linha.faltantes} faltante(s)
          </Badge>
        )}

        {linha.faltantes > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => setAberto((v) => !v)}
          >
            {aberto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {aberto ? "Ocultar" : "Ver faltantes"}
          </Button>
        )}
      </div>

      {aberto && linha.faltantes > 0 && (
        <div className="border-t bg-muted/30 px-4 py-3">
          <ul className="space-y-1 text-sm">
            {linha.notasFaltantes.map((nota) => (
              <li key={nota.arquivo} className="flex items-start gap-2">
                <Badge variant="secondary" className="mt-0.5 shrink-0 text-[0.6rem]">
                  {nota.tipo === "SALDO_REMANESCENTE" ? "SR" : "ANT"}
                </Badge>
                <span>{nota.descricao}</span>
              </li>
            ))}
          </ul>
          {linha.faltantesTruncados && (
            <p className="mt-2 text-xs text-muted-foreground">
              Mostrando as primeiras {linha.notasFaltantes.length} de {linha.faltantes} notas faltantes.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function CompletudePanel({ data, isLoading, isFetching, isError, onRefresh }: CompletudePanelProps) {
  if (isLoading) {
    return (
      <div className="mt-6 flex items-center justify-center gap-3 rounded-md border py-10 text-muted-foreground">
        <RefreshCcw className="h-5 w-5 animate-spin" />
        Comparando as notas do banco com o que já está no S3...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mt-6 flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-4 text-sm text-destructive">
        <span className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          Não foi possível verificar a completude das notas.
        </span>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const { total, porOps, periodo, verificadoEm, isCurrentWeek, domingoEfetivo } = data;
  const ratioGeral = pct(total.baixadas, total.esperadas);
  const tudoCompleto = total.faltantes === 0 && total.esperadas > 0;

  if (total.esperadas === 0) {
    return (
      <div className="mt-6 rounded-md border px-4 py-6 text-center text-sm text-muted-foreground">
        Nenhuma nota emitida na semana de {formatDatePTBR(periodo.segunda)} a {formatDatePTBR(periodo.domingo)}.
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {/* Cabeçalho: período verificado + recarregar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Semana de <strong className="text-foreground">{formatDatePTBR(periodo.segunda)}</strong> a{" "}
          <strong className="text-foreground">{formatDatePTBR(isCurrentWeek ? domingoEfetivo : periodo.domingo)}</strong>
          {isCurrentWeek && " (semana em aberto — até hoje)"} · verificado às {formatDateTime(verificadoEm)}
        </p>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching}>
          <RefreshCcw className={`mr-2 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          {isFetching ? "Verificando..." : "Reverificar"}
        </Button>
      </div>

      {/* Número-herói + contagens */}
      <div
        className={`rounded-lg border p-5 ${
          tudoCompleto
            ? "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20"
            : "border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20"
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {tudoCompleto ? (
              <CheckCircle2 className="h-10 w-10 shrink-0 text-emerald-600 dark:text-emerald-500" />
            ) : (
              <AlertTriangle className="h-10 w-10 shrink-0 text-amber-600 dark:text-amber-500" />
            )}
            <div>
              <div className="text-4xl font-bold tabular-nums text-foreground">
                {Math.round(ratioGeral * 100)}%
              </div>
              <p className="text-sm text-muted-foreground">
                {tudoCompleto
                  ? "Todas as notas da semana já estão baixadas"
                  : `${total.faltantes} nota(s) ainda não baixada(s)`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 sm:gap-6">
            <div className="text-right">
              <div className="flex items-center justify-end gap-1.5 text-2xl font-bold tabular-nums text-foreground">
                <Database className="h-4 w-4 text-muted-foreground" />
                {total.esperadas}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">Esperadas (banco)</p>
            </div>
            <div className="text-right">
              <div className="flex items-center justify-end gap-1.5 text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-500">
                <HardDriveDownload className="h-4 w-4" />
                {total.baixadas}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">Baixadas (S3)</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold tabular-nums text-amber-600 dark:text-amber-500">
                {total.faltantes}
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">Faltantes</p>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <Meter ratio={ratioGeral} completo={tudoCompleto} />
        </div>
      </div>

      {/* Por franquia */}
      <div className="overflow-hidden rounded-md border">
        <div className="border-b bg-muted/50 px-4 py-2 text-xs font-semibold uppercase text-muted-foreground">
          Por franquia ({porOps.length})
        </div>
        {porOps.map((linha) => (
          <OpsRow key={linha.ops} linha={linha} />
        ))}
      </div>
    </div>
  );
}
